import { Box, Button, Divider, FormLabel, Pagination, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useFormik } from 'formik';
import { ChangeEvent, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import utilityApi from '~/apis/utility';
import { AddNewIcon, ExportFillIcon } from '~/assets/icons';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { cast, clearInputFilter, exportExcel, handleApiError, notify } from '~/utils/helper';
import DistributorTable from './component/DistributorTable';
import styles from './Distributor.module.scss';
import { IAccount, IGetAllParams } from '~/utils/interface/account';
import accountApi from '~/apis/account';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import dayjs from 'dayjs';
import { ROLE_DISTRIBUTOR } from '~/utils/constants/account';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, DIALOG_MODE_VIEW } from '~/utils/constants/common';
import EditDistributorDialog from './component/edit-distributor-dialog/EditDistributorDialog';
import TransferDistributorDialog from './component/send-notification-dialog/SendNotificationDialog';

dayjs.extend(customParseFormat);

const cx = classNames.bind(styles);

const initialValues: IGetAllParams = {
  distributor_level: '',
  date_to: '',
  order_by: {
    field: '',
    type: '',
  },
  page_index: 1,
  page_size: 10,
};

const Distributor = () => {
  const { t } = useTranslation('admin/distributor');

  const [data, setAccount] = useState<IAccount[]>([]);
  const [distributorOptions, setDistributorOptions] = useState<any[]>([]);

  const [totalItems, setTotalItems] = useState<number>(0);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [distributorIds, setDistributorIds] = useState<Array<string>>([]);

  const [distributor, setDistributor] = useState<IAccount>();
  const [isOpenDeleteDistributorDialog, setIsOpenDeleteDistributorDialog] = useState<boolean>(false);

  const [isOpenEditDistributorDialog, setIsOpenEditDistributorDialog] = useState<boolean>(false);
  const [distributorDialogMode, setDistributorDialogMode] = useState<string>('');

  const [isOpenTransferDistributorDialog, setIsOpenTransferDistributorDialog] = useState<boolean>(false);

  useEffect(() => {
    if (refreshData) {
      handleGetDistributors();
      setRefreshData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData]);

  useEffect(() => {
    (async () => {
      try {
        const response = await utilityApi.getListDistributorLevels();
        setDistributorOptions(
          response.data.map((item) => {
            return {
              id: item.level_id,
              name: item.level_name,
            };
          })
        );
      } catch (error) {
        handleApiError(error);
      }
    })();
  }, []);

  const form = useFormik<IGetAllParams>({
    initialValues,
    onSubmit: async () => {
      handleGetDistributors();
    },
  });

  useEffect(() => {
    handleGetDistributors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.page_index, form.values.page_size]);

  useEffect(() => {
    form.handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values]);

  const getDistributors = async (overrideParams = {}) => {
    try {
      return (
        await accountApi.getByRoleList(ROLE_DISTRIBUTOR, {
          ...form.values,
          distributor_level: form.values.distributor_level ? Number(form.values.distributor_level) : 0,
          created_at: form.values.created_at
            ? dayjs(form.values.created_at, 'DD/MM/YYYY').format('YYYY-MM-DD')
            : undefined,
          ...overrideParams,
        })
      ).data;
    } catch (error) {
      handleApiError(error);
      return { total: 0, data: [] };
    }
  };

  const handleGetDistributors = async () => {
    const response = await getDistributors();
    setTotalItems(response.total);
    setAccount(response.data);
  };

  const handleCheckAll = async (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      const res = await getDistributors({ page_index: 1, page_size: totalItems });
      setDistributorIds(res.data?.map((account: IAccount) => cast(account.username)));
    } else {
      setDistributorIds([]);
    }
  };

  const handleCheck = async (row: IAccount, checked: boolean) => {
    if (checked) {
      setDistributorIds((prev) => [...(prev ?? []), ...[cast(row.username)]]);
    } else {
      setDistributorIds(distributorIds.filter((account) => account !== row.username));
    }
  };

  const exportFile = async () => {
    if (!distributorIds.length) return notify('error', t('emptyExportFile'));
    const response = await getDistributors({ page_index: 1, page_size: totalItems });

    let res = response.data;
    if (distributorIds.length) {
      const filterSet = new Set(distributorIds);
      res = response.data?.filter((item) => filterSet.has(cast(item.username)));
      if (!res.length) {
        setDistributorIds([]);
        res = response.data;
      }
    }

    const columns = [
      { header: t('table.username'), key: 'username', width: 20 },
      { header: t('table.name'), key: 'name', width: 15 },
      { header: t('table.address'), key: 'address', width: 20 },
      { header: t('table.representative'), key: 'representative', width: 15 },
      { header: t('table.phone'), key: 'phone', width: 15 },
      { header: t('table.email'), key: 'email', width: 15 },
      { header: t('table.total_agents'), key: 'total_agents' },
      { header: t('table.total_devices'), key: 'total_devices' },
      { header: t('table.total_users'), key: 'total_users' },
      { header: t('table.created_at'), key: 'created_at', width: 20 },
    ];

    const data = res?.map((row, index) => ({
      ...row,
      no: index + 1,
    }));

    exportExcel(columns, data, t('file_name'));
  };

  const handleDeleteDistributor = async (needRefresh: boolean = false) => {
    try {
      if (distributor) {
        await accountApi.deleteByRole({ role: ROLE_DISTRIBUTOR, username: distributor.username });
        if (needRefresh) {
          handleGetDistributors();
        }
        notify('success', t('deleteDialog.deleteSuccess'));
        setIsOpenDeleteDistributorDialog(false);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleSetIsOpenEditDistributorDialog = (isOpen: boolean = false, needRefresh: boolean = false) => {
    setIsOpenEditDistributorDialog(isOpen);
    if (needRefresh) {
      handleGetDistributors();
    }
  };

  return (
    <Box className={cx('w-full', 'distributor-screen')}>
      <Box className={cx('box-container')}>
        <Box className={cx('column-text-field')}>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='distributor'>{t('level')}</FormLabel>
            <BaseSelect
              options={distributorOptions}
              value={form.values.distributor_level}
              placeholder={t('select')}
              name='distributor_level'
              isSearchEnable
              onChange={form.handleChange}
              displayInvalidValue
              isDisplayAllOption
            />
          </Box>
        </Box>
        <Box className='flex flex-row items-center gap-[20px]'>
          <FormLabel htmlFor='date_from'>{t('registration_date')}</FormLabel>
          <Box className={cx('column-text-field')}>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='date_from'>{t('date_from')}</FormLabel>
              <BaseDatePicker
                portalId='datePicker-portalId'
                value={form.values.date_from}
                dateFormat='YYYY-MM-DD'
                onChange={(date) => form.setFieldValue('date_from', dayjs(date).format('YYYY-MM-DD'))}
                placeholder={t('select')}
                calendarProps={form.values.date_to ? { maxDate: new Date(form.values.date_to) } : {}}
              />
            </Box>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='date_to'>{t('date_to')}</FormLabel>
              <BaseDatePicker
                portalId='datePicker-portalId'
                value={form.values.date_to}
                dateFormat='YYYY-MM-DD'
                onChange={(date) => form.setFieldValue('date_to', dayjs(date).format('YYYY-MM-DD'))}
                placeholder={t('select')}
                calendarProps={form.values.date_from ? { minDate: new Date(form.values.date_from) } : {}}
              />
            </Box>
          </Box>
        </Box>
        <Box className=' text-center'>
          <Button
            className='bg-[#ED1002] rounded-[20px] text-white w-[130px] h-[35px] hover:bg-[#ED1002] hover:opacity-80'
            onClick={() => {
              clearInputFilter();
              form.resetForm();
            }}
          >
            {t('clearFilter')}
          </Button>
        </Box>
      </Box>
      <Divider
        sx={{
          borderColor: '#005F73',
          borderBottomWidth: 1,
          width: '100%',
          my: '14px',
        }}
      ></Divider>
      <Box className={cx('table-container')}>
        <Box className='bg-[#ED1002] flex py-2 px-5 justify-between items-center'>
          <Typography className='text-[16px] font-bold text-[white]'>{t('title')}</Typography>
          <Box className='flex pr-10'>
            <Button
              startIcon={<AddNewIcon />}
              className='text-white'
              onClick={() => {
                setDistributorDialogMode(DIALOG_MODE_CREATE);
                setIsOpenEditDistributorDialog(true);
              }}
            >
              {t('add')}
            </Button>

            <Button startIcon={<ExportFillIcon />} className='text-white' onClick={exportFile}>
              {t('export')}
            </Button>
          </Box>
        </Box>
        <DistributorTable
          pageSize={form.values.page_size ?? 10}
          data={data}
          form={form}
          onSetRefreshData={setRefreshData}
          distributorIds={distributorIds}
          handleCheck={handleCheck}
          handleCheckAll={handleCheckAll}
          onTransfer={(row) => {
            setDistributor(row);
            setIsOpenTransferDistributorDialog(true);
          }}
          onDetail={(row) => {
            setDistributor(row);
            setIsOpenEditDistributorDialog(true);
            setDistributorDialogMode(DIALOG_MODE_VIEW);
          }}
          onEdit={(row) => {
            setDistributor(row);
            setIsOpenEditDistributorDialog(true);
            setDistributorDialogMode(DIALOG_MODE_EDIT);
          }}
          onDelete={(row) => {
            setDistributor(row);
            setIsOpenDeleteDistributorDialog(true);
          }}
        />
      </Box>
      <Box className={cx('flex items-center justify-end gap-10 mt-5', 'pagination-box')}>
        <Typography>
          {t('sum')}: <span className='font-bold text-[#005f73]'>{totalItems}</span> {t('record')}
        </Typography>
        <Box className='w-[150px]'>
          <BaseSelect
            options={[
              { id: 10, name: '10' },
              { id: 20, name: '20' },
              { id: 50, name: '50' },
              { id: 100, name: '100' },
              { id: 250, name: '250' },
              { id: 500, name: '500' },
            ]}
            value={form.values.page_size}
            onChange={(e) => {
              form.setFieldValue('page_size', e.target.value);
              form.setFieldValue('page_index', 1);
            }}
            renderSelected={(option: any) => (
              <Typography className='px-2'>
                {option.name}/{t('page').toLowerCase()}
              </Typography>
            )}
          />
        </Box>
        <Pagination
          count={Math.max(1, Math.ceil(totalItems / (form.values.page_size ?? 1)))}
          variant='outlined'
          page={form.values.page_index ?? 1}
          onChange={(e, value: number) => {
            form.setFieldValue('page_index', value);
          }}
        />
        <Box className='flex items-center gap-3'>
          <Typography>{t('page')}</Typography>
          <Box className='rounded-[20px] border border-[#ED1002] text-center py-2 px-7'>{form.values.page_index}</Box>
        </Box>
      </Box>

      {isOpenEditDistributorDialog && (
        <EditDistributorDialog
          data={distributor}
          mode={distributorDialogMode}
          open={isOpenEditDistributorDialog}
          setOpen={handleSetIsOpenEditDistributorDialog}
        />
      )}

      {isOpenTransferDistributorDialog && (
        <TransferDistributorDialog
          open={isOpenTransferDistributorDialog}
          setOpen={setIsOpenTransferDistributorDialog}
          data={distributor}
        />
      )}

      {isOpenDeleteDistributorDialog && (
        <ConfirmDialog
          open={isOpenDeleteDistributorDialog}
          title={t('deleteDialog.title')}
          description={t('deleteDialog.description', {
            name: distributor?.name,
          })}
          onConfirm={() => handleDeleteDistributor(true)}
          onCancel={() => setIsOpenDeleteDistributorDialog(false)}
        />
      )}
    </Box>
  );
};

export default memo(Distributor);
